import { useEffect, useState } from 'react';
import { Button, TextField, Grid, MenuItem } from '@material-ui/core';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import { withdrawService } from '#root/services';
import { GuveryDialog } from '#root/ui-components';
import BankAccountDTO from '#root/interfaces/BankAccountDTO';
import { ArrowBackIcon } from '#root/icons';
import BankAccountSelector from './BankAccountSelector';
import styles from './earningsNewDialog.module.scss';

interface EarningsNewDialogProps {
  open: boolean;
  onClose: () => void;
  earnings?: any;
  nextWithdrawSequence?: number;
  createMessage?: any;
  handleLoading?: any;
}

const banks = [
  {
    value: 'INTERBANK',
    label: 'Interbank',
  },
  {
    value: 'BCP',
    label: 'BCP',
  }
];

const bankAccountTypes = [
  {
    value: 'Cuenta Bancaria',
    label: 'Cuenta de ahorro en dólares',
  },
  {
    value: 'Tarjeta de Crédito',
    label: 'Tarjeta de Crédito',
  },
];

// Helper: Genera un ID único para cada cuenta
const getAccountId = (account: BankAccountDTO) => {
  return `${account.bank_name}-${account.account_type}-${account.account_number}-${account.account_holder}`;
};

const EarningsNewDialog = (props: EarningsNewDialogProps) => {
  const { open = false, onClose, nextWithdrawSequence, createMessage, handleLoading } = props;

  // State for mode selection (saved account vs new account)
  const [mode, setMode] = useState<'saved' | 'new'>('saved');

  // State for saved bank accounts
  const [bankAccounts, setBankAccounts] = useState<BankAccountDTO[]>([]);
  const [loadingAccounts, setLoadingAccounts] = useState(true);
  const [selectedAccountNumber, setSelectedAccountNumber] = useState<string | null>(null);

  const getValidationSchema = () =>
    Yup.object().shape({
      name: Yup.string().min(4, 'Al menos 4 letras o dejar vacío.'),
      bank: mode === 'new' ? Yup.string().required('Campo requerido') : Yup.string(),
      bank_account_type:
        mode === 'new' ? Yup.string().required('Campo requerido') : Yup.string(),
      bank_account_number:
        mode === 'new'
          ? Yup.string().min(8, '¡Demasiado corto!').required('Campo requerido')
          : Yup.string(),
      bank_account_holder:
        mode === 'new' ? Yup.string().required('Campo requerido') : Yup.string(),
    });

  const formik = useFormik({
    initialValues: {
      name: '',
      bank: '',
      bank_account_type: '',
      bank_account_number: '',
      bank_account_holder: '',
    },
    validationSchema: getValidationSchema(),
    onSubmit: (values) => {
      handleSubmitWithdrawal(values);
      formik.setSubmitting(false);
    },
  });

  // Fetch bank accounts when dialog opens
  useEffect(() => {
    if (open) {
      fetchBankAccounts();
    }
  }, [open]);

  // Auto-select first account when accounts load
  useEffect(() => {
    if (bankAccounts.length > 0 && !selectedAccountNumber) {
      setSelectedAccountNumber(getAccountId(bankAccounts[0]));
    }
  }, [bankAccounts, selectedAccountNumber]);

  // Auto-switch to "new" mode if no saved accounts
  useEffect(() => {
    if (!loadingAccounts && bankAccounts.length === 0) {
      setMode('new');
    }
  }, [loadingAccounts, bankAccounts]);

  const fetchBankAccounts = async () => {
    setLoadingAccounts(true);
    const res = await withdrawService.getBankAccounts();

    if (!res.error && res.data?.items) {
      setBankAccounts(res.data.items);
    } else {
      setBankAccounts([]);
    }

    setLoadingAccounts(false);
  };

  const handleClean = () => {
    formik.resetForm({
      values: {
        name: '',
        bank: '',
        bank_account_type: '',
        bank_account_number: '',
        bank_account_holder: '',
      },
    });
    setMode('saved');
    setSelectedAccountNumber(null);
  };

  const handleSubmitWithdrawal = async (values) => {
    let body: any = {
      name: values.name,
    };

    if (mode === 'saved') {
      // Use saved account
      if (!selectedAccountNumber) {
        createMessage.error('Por favor selecciona una cuenta guardada');
        return;
      }

      // Buscar cuenta por ID compuesto o por account_number (backward compatibility)
      const selectedAccount = bankAccounts.find(
        (acc) => getAccountId(acc) === selectedAccountNumber || acc.account_number === selectedAccountNumber,
      );

      if (!selectedAccount) {
        createMessage.error('Cuenta seleccionada no válida');
        return;
      }

      body = {
        ...body,
        bank: selectedAccount.bank_name,
        bank_account_type: selectedAccount.account_type,
        bank_account_number: selectedAccount.account_number,
        bank_account_holder: selectedAccount.account_holder,
      };
    } else {
      // Use new account
      if (!values.bank || !values.bank_account_number) {
        createMessage.error('Por favor completa todos los campos requeridos');
        return;
      }

      let fixed_bank_account_number = values.bank_account_number;
      if (fixed_bank_account_number) {
        fixed_bank_account_number = fixed_bank_account_number
          .replace(/-/g, '')
          .replace(/ /g, '');
      }

      body = {
        ...body,
        bank: values.bank,
        bank_account_type: values.bank_account_type,
        bank_account_number: fixed_bank_account_number,
        bank_account_holder: values.bank_account_holder,
      };
    }

    const res = await withdrawService.saveNew(body);
    if (res.error) {
      createMessage.error(res.message);
      return;
    }

    createMessage.success(res.message);

    // Refresh bank accounts list for next time
    fetchBankAccounts();

    handleLoading();
    onClose();
  };

  useEffect(() => {
    if (!open) {
      handleClean();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <GuveryDialog
      open={open}
      onClose={onClose}
      disableActions
      title="Retirar Ganancias"
      closeText="Cerrar">
      <div className={styles.dialogContent}>
        <form onSubmit={formik.handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <p>
                <strong>Importante: </strong>
                Tu cuenta bancaria debe haber sido abierta en Lima Metropolitana, ya que sino, el
                banco cobrará una comisión por transferencia interplaza y será asumida por el
                viajero.
              </p>
            </Grid>

            <Grid item xs={12}>
              <h2>Mi Retiro #{nextWithdrawSequence}</h2>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                variant="outlined"
                label="Descripción opcional"
                type="text"
                id="name"
                name="name"
                value={formik.values.name}
                onChange={formik.handleChange}
                error={formik.touched.name && Boolean(formik.errors.name)}
                helperText={formik.touched.name && formik.errors.name}
              />
            </Grid>

            {/* Saved accounts selector OR collapsed button */}
            {mode === 'saved' ? (
              <Grid item xs={12}>
                <BankAccountSelector
                  accounts={bankAccounts}
                  selectedAccount={selectedAccountNumber}
                  onSelect={setSelectedAccountNumber}
                  onAddNew={() => setMode('new')}
                  loading={loadingAccounts}
                />
              </Grid>
            ) : (
              // Show collapsed button if there are saved accounts
              bankAccounts.length > 0 && (
                <Grid item xs={12}>
                  <button
                    type="button"
                    className={styles.collapsedButton}
                    onClick={() => setMode('saved')}>
                    <ArrowBackIcon className={styles.backIcon} />
                    <span>Ver cuentas guardadas ({bankAccounts.length})</span>
                  </button>
                </Grid>
              )
            )}

            {/* New account form */}
            {mode === 'new' && (
              <>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    label="* Banco"
                    select
                    id="bank"
                    name="bank"
                    value={formik.values.bank}
                    onChange={formik.handleChange}
                    error={Boolean(formik.touched.bank && formik.errors.bank)}
                    helperText={formik.touched.bank && formik.errors.bank}>
                    {banks?.map((item, index) => (
                      <MenuItem key={index} value={item.value}>
                        {item.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    label="* Tipo de Cuenta"
                    select
                    id="bank_account_type"
                    name="bank_account_type"
                    value={formik.values.bank_account_type}
                    onChange={formik.handleChange}
                    error={Boolean(
                      formik.touched.bank_account_type && formik.errors.bank_account_type,
                    )}
                    helperText={
                      formik.touched.bank_account_type && formik.errors.bank_account_type
                    }>
                    {bankAccountTypes?.map((item, index) => (
                      <MenuItem key={index} value={item.value}>
                        {item.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    label="* Numero de cuenta"
                    id="bank_account_number"
                    name="bank_account_number"
                    value={formik.values.bank_account_number}
                    onChange={formik.handleChange}
                    error={Boolean(
                      formik.touched.bank_account_number && formik.errors.bank_account_number,
                    )}
                    helperText={
                      formik.touched.bank_account_number && formik.errors.bank_account_number
                    }
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    label="* Titular de la cuenta"
                    type="text"
                    id="bank_account_holder"
                    name="bank_account_holder"
                    value={formik.values.bank_account_holder}
                    onChange={formik.handleChange}
                    error={
                      formik.touched.bank_account_holder &&
                      Boolean(formik.errors.bank_account_holder)
                    }
                    helperText={
                      formik.touched.bank_account_holder && formik.errors.bank_account_holder
                    }
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <Button
                fullWidth
                disableElevation
                type="submit"
                variant="contained"
                color="primary"
                disabled={mode === 'saved' && !selectedAccountNumber}>
                Solicitar Retiro
              </Button>
            </Grid>
          </Grid>
        </form>
      </div>
    </GuveryDialog>
  );
};

export default EarningsNewDialog;
